/**
 * Scaling rule parser for the proof. grammar (Track 2.A).
 *
 * Two forms per extensibility-design.md §3.3:
 *
 * 1. Per-variable suffix on a labelled line: `yeast: 2g @scale: 0.5`
 *    Returns a ScalingRuleNode with a coefficient or expression.
 *
 * 2. Global scale directive: `@scale x1.5` or `@scale from=cylinder(...) to=cylinder(...)`
 *    Returns a ScaleDirectiveNode.
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import type { ScalingRuleNode, ScaleDirectiveNode, ScopePath } from "../ast/types.js";
import { parseExpression } from "./expressions.js";

// ---------------------------------------------------------------------------
// Per-variable scaling rule suffix
// ---------------------------------------------------------------------------

export interface ScalingRuleParseResult {
  rule: ScalingRuleNode | null;
  errors: string[];
}

/**
 * Parse the content after `@scale:` on a labelled line.
 *
 * Input examples:
 *   `0.5`         — literal coefficient
 *   `=log(x + 1)` — expression
 */
export function parseScalingRuleSuffix(content: string): ScalingRuleParseResult {
  const trimmed = content.trim();
  const errors: string[] = [];

  if (trimmed === "") {
    return { rule: null, errors: ["@scale: suffix is empty"] };
  }

  if (trimmed.startsWith("=")) {
    // Expression form
    const result = parseExpression(trimmed);
    if (result.node === null) {
      return { rule: null, errors: result.errors.map((e) => `@scale: expression: ${e}`) };
    }
    const rule: ScalingRuleNode = { kind: "scaling-rule", expression: result.node };
    return { rule, errors };
  }

  // Numeric coefficient form
  const coeff = parseFloat(trimmed);
  if (!isFinite(coeff)) {
    return { rule: null, errors: [`@scale: invalid coefficient '${trimmed}'`] };
  }
  const rule: ScalingRuleNode = { kind: "scaling-rule", coefficient: coeff };
  return { rule, errors };
}

// ---------------------------------------------------------------------------
// Strip @scale: suffix from a value string
// ---------------------------------------------------------------------------

const SCALE_SUFFIX_RE = /\s+@scale:\s*(.+)$/u;

export interface StripScaleResult {
  /** The value string without the @scale: suffix. */
  cleanValue: string;
  /** The @scale: content (if present). Undefined when no suffix found. */
  scaleSuffix: string | undefined;
}

/**
 * Strip `@scale: ...` suffix from a labelled-line rawValue.
 * Returns the clean value and the suffix content if present.
 */
export function stripScaleSuffix(rawValue: string): StripScaleResult {
  const m = SCALE_SUFFIX_RE.exec(rawValue);
  if (m === null) return { cleanValue: rawValue, scaleSuffix: undefined };
  return {
    cleanValue: rawValue.slice(0, m.index).trim(),
    scaleSuffix: m[1]?.trim() ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// Global scale directive args parser
// ---------------------------------------------------------------------------

export interface ScaleDirectiveParseResult {
  node: ScaleDirectiveNode | null;
  errors: string[];
}

// Factor-form patterns. The multiplier marker `x` (or `×`) may sit before or
// after the number — `x1.5` and `1.5x` are equivalent — but never both and
// never repeated: `x1.5x`, `1.5xx`, `xx1.5` are invalid factors.
const FACTOR_NUM = String.raw`(\d+(?:\.\d+)?|\.\d+)`;
const PREFIX_FACTOR_RE = new RegExp(`^[x×]\\s*${FACTOR_NUM}$`, "u");
const SUFFIX_FACTOR_RE = new RegExp(`^${FACTOR_NUM}\\s*[x×]$`, "u");
const BARE_FACTOR_RE = new RegExp(`^${FACTOR_NUM}$`, "u");
const DIVIDE_FACTOR_RE = new RegExp(`^[/÷]\\s*${FACTOR_NUM}$`, "u");

/**
 * Parse the args portion of an `@scale` directive line.
 *
 * Input examples (after stripping `@scale `):
 *   `x1.5` / `1.5x` / `1.5`
 *   `/2`
 *   `from=cylinder(22cm, 5cm) to=cylinder(26cm, 5cm)`
 */
export function parseScaleDirective(args: string, scopePath: ScopePath): ScaleDirectiveParseResult {
  const trimmed = args.trim();
  const errors: string[] = [];

  if (trimmed === "") {
    const node: ScaleDirectiveNode = { kind: "scale-directive", scopePath };
    return { node, errors };
  }

  // `from=... to=...` form (geometry-based scale)
  if (trimmed.startsWith("from=")) {
    return parseGeometryScale(trimmed, scopePath);
  }

  // `x1.5`, `1.5x`, bare `1.5`, or `/2` — simple factor
  const divideMatch = DIVIDE_FACTOR_RE.exec(trimmed);
  const factorMatch =
    PREFIX_FACTOR_RE.exec(trimmed) ??
    SUFFIX_FACTOR_RE.exec(trimmed) ??
    BARE_FACTOR_RE.exec(trimmed) ??
    divideMatch;
  const factor = factorMatch === null ? NaN : parseFloat(factorMatch[1] ?? "");
  if (!isFinite(factor) || factor <= 0) {
    errors.push(`@scale: invalid factor '${trimmed}'`);
    const node: ScaleDirectiveNode = { kind: "scale-directive", scopePath };
    return { node, errors };
  }

  const effectiveFactor = divideMatch !== null ? 1 / factor : factor;

  const node: ScaleDirectiveNode = {
    kind: "scale-directive",
    factor: effectiveFactor,
    scopePath,
  };
  return { node, errors };
}

function parseGeometryScale(args: string, scopePath: ScopePath): ScaleDirectiveParseResult {
  const errors: string[] = [];

  // Extract from=... and to=...
  // We split on `to=` but need to handle nested parens
  const fromIdx = args.indexOf("from=");
  const toIdx = args.indexOf(" to=");

  if (fromIdx < 0 || toIdx < 0) {
    errors.push(`@scale: geometry form requires 'from=... to=...'`);
    const node: ScaleDirectiveNode = { kind: "scale-directive", scopePath };
    return { node, errors };
  }

  const fromExprStr = args.slice(fromIdx + "from=".length, toIdx).trim();
  const toExprStr = args.slice(toIdx + " to=".length).trim();

  const fromResult = parseExpression("=" + fromExprStr);
  const toResult = parseExpression("=" + toExprStr);

  errors.push(...fromResult.errors, ...toResult.errors);

  const node: ScaleDirectiveNode = {
    kind: "scale-directive",
    ...(fromResult.node !== null ? { fromExpr: fromResult.node } : {}),
    ...(toResult.node !== null ? { toExpr: toResult.node } : {}),
    scopePath,
  };

  return { node, errors };
}
